import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import SidebarSeccion from '../../components/SidebarSeccion';
import { useAuth, API_URL } from '../../context/AuthContext';
import { getTema } from '../../theme';
import { TrashIcon } from '@heroicons/react/24/outline';

const CATEGORIAS = ['Writing', 'Listening', 'Reading', 'Speaking'];

export default function Temario({ tipo, nivel }) {
  const { usuario } = useAuth();
  const tema = getTema(tipo);
  const nivelDisplay = nivel;
  const esProfesor = ['profesor','admin'].includes(usuario?.rol);
  const [temas, setTemas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [categoriaActiva, setCategoriaActiva] = useState('Writing');
  const [form, setForm] = useState({ titulo: '', descripcion: '', orden: 0, categoria: 'Writing' });
  const [archivo, setArchivo] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const fileRef = useRef();
  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const cargar = async () => {
    try {
      setCargando(true);
      const res = await axios.get(`${API_URL}/temario?tipo=${tipo}&nivel=${nivel}`);
      setTemas(res.data);
    } catch (e) {
      setError('Error al cargar el temario');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [tipo, nivel]);

  const handleDragStart = (e, index) => {
    dragIndex.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(index);
  };
  const handleDrop = async (e, toIndex) => {
    e.preventDefault();
    setDragOver(null);
    const fromIndex = dragIndex.current;
    if (fromIndex === null || fromIndex === toIndex) return;
    const lista = [...temasFiltradosOrdenados()];
    const [moved] = lista.splice(fromIndex, 1);
    lista.splice(toIndex, 0, moved);
    const updated = lista.map((t, i) => ({ ...t, orden: i + 1 }));
    setTemas(prev => {
      const otros = prev.filter(t => t.categoria !== categoriaActiva);
      return [...otros, ...updated];
    });
    try {
      await axios.put(`${API_URL}/temario/reordenar`, {
        items: updated.map(t => ({ id: t.id, orden: t.orden })),
      });
    } catch { cargar(); }
  };
  const handleDragEnd = () => { setDragOver(null); dragIndex.current = null; };

  const temasFiltradosOrdenados = () =>
    temas.filter(t => !t.categoria || t.categoria === categoriaActiva)
         .sort((a, b) => (a.orden || 0) - (b.orden || 0));

  const handleSubir = async (e) => {
    e.preventDefault();
    if (!form.titulo) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('titulo', form.titulo);
      fd.append('descripcion', form.descripcion);
      fd.append('orden', form.orden);
      fd.append('tipo', tipo);
      fd.append('nivel', nivel);
      fd.append('categoria', form.categoria);
      if (archivo) fd.append('archivo', archivo);
      await axios.post(`${API_URL}/temario`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm({ titulo: '', descripcion: '', orden: 0, categoria: categoriaActiva });
      setArchivo(null);
      setMostrarForm(false);
      if (fileRef.current) fileRef.current.value = '';
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al subir');
    } finally {
      setSubiendo(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este tema?')) return;
    try {
      await axios.delete(`${API_URL}/temario/${id}`);
      cargar();
    } catch { setError('Error al eliminar'); }
  };

  const temasFiltrados = temasFiltradosOrdenados();

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '8px',
    background: tema.bg, border: `1px solid ${tema.border}`,
    color: tema.text, fontSize: '14px',
  };

  return (
    <div style={{ minHeight: '100vh', background: tema.bg }}>
      <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivelDisplay} />
      <div className="page-layout">
        <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} nivelDisplay={nivelDisplay} />
        <main className="page-content">
          <div className="fade-in">
            <div className="section-header">
              <div>
                <h2 style={{ color: tema.text, fontSize: '26px', marginBottom: '4px' }}>
                  📚 Temario
                </h2>
                <p style={{ color: tema.muted, fontSize: '14px' }}>
                  Material de estudio para {nivelDisplay}
                </p>
              </div>
              {esProfesor && (
                <button
                  onClick={() => {
                    setMostrarForm(!mostrarForm);
                    setForm({ titulo: '', descripcion: '', orden: 0, categoria: categoriaActiva });
                  }}
                  style={{
                    background: tema.primary, border: 'none', color: '#fff',
                    padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {mostrarForm ? '✕ Cancelar' : '+ Añadir tema'}
                </button>
              )}
            </div>

            {/* Tabs de categoría */}
            <div className="tabs-row" style={{ borderBottom: `1px solid ${tema.border}` }}>
              {CATEGORIAS.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategoriaActiva(cat)}
                  style={{
                    background: 'none', border: 'none',
                    borderBottom: categoriaActiva === cat ? `2px solid ${tema.accent}` : '2px solid transparent',
                    color: categoriaActiva === cat ? tema.accent : tema.muted,
                    padding: '10px 20px', fontSize: '14px', fontWeight: categoriaActiva === cat ? 700 : 400,
                    cursor: 'pointer', transition: 'all 0.15s',
                    marginBottom: '-1px',
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Formulario subir */}
            {esProfesor && mostrarForm && (
              <form onSubmit={handleSubir} style={{
                background: tema.bgCard, border: `1px solid ${tema.border}`,
                borderRadius: '12px', padding: '24px', marginBottom: '28px',
              }}>
                <h3 style={{ color: tema.accent, marginBottom: '16px', fontSize: '16px', fontFamily: 'Inter, sans-serif' }}>
                  Nuevo tema en {form.categoria}
                </h3>
                <div className="form-grid-2">
                  <div>
                    <label style={{ color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '6px' }}>Categoría *</label>
                    <select style={inputStyle} value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>
                      {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '6px' }}>Título *</label>
                    <input style={inputStyle} value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} required />
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '6px' }}>Descripción</label>
                  <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }} value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '6px' }}>Archivo PDF</label>
                  <input ref={fileRef} type="file" accept=".pdf" onChange={e => setArchivo(e.target.files[0])}
                    style={{ color: tema.text, fontSize: '13px' }} />
                </div>
                <button type="submit" disabled={subiendo} style={{
                  background: tema.primary, border: 'none', color: '#fff',
                  padding: '10px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                }}>
                  {subiendo ? 'Subiendo...' : 'Guardar tema'}
                </button>
              </form>
            )}

            {error && (
              <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '8px', padding: '12px', color: '#e74c3c', marginBottom: '20px', fontSize: '14px' }}>
                {error}
              </div>
            )}

            {cargando ? (
              <div style={{ color: tema.muted, textAlign: 'center', padding: '48px' }}>Cargando...</div>
            ) : temasFiltrados.length === 0 ? (
              <div style={{ color: tema.muted, textAlign: 'center', padding: '64px 24px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📂</div>
                <p style={{ fontSize: '16px' }}>No hay contenido de {categoriaActiva} aún</p>
                {esProfesor && <p style={{ fontSize: '13px', marginTop: '8px' }}>Usa el botón de arriba para añadir el primer tema</p>}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {esProfesor && <p style={{ color: tema.muted, fontSize: '12px', textAlign: 'center', marginBottom: '4px' }}>⠿ Arrastra los temas para reordenarlos</p>}
                {temasFiltrados.map((t, index) => (
                  <div key={t.id}
                    draggable={esProfesor}
                    onDragStart={esProfesor ? (e) => handleDragStart(e, index) : undefined}
                    onDragOver={esProfesor ? (e) => handleDragOver(e, index) : undefined}
                    onDrop={esProfesor ? (e) => handleDrop(e, index) : undefined}
                    onDragEnd={esProfesor ? handleDragEnd : undefined}
                    style={{
                      background: tema.bgCard,
                      border: `2px solid ${dragOver === index ? tema.accent : tema.border}`,
                      borderRadius: '12px', padding: '20px 24px',
                      transition: 'border-color 0.15s, transform 0.1s',
                      cursor: esProfesor ? 'grab' : 'default',
                      transform: dragOver === index ? 'scale(1.01)' : 'scale(1)',
                    }}>
                    <div className="card-row">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        {esProfesor && <span style={{ color: tema.muted, fontSize: '16px', cursor: 'grab', flexShrink: 0 }}>⠿</span>}
                        {t.orden > 0 && (
                          <span style={{ background: `${tema.accent}22`, color: tema.accent, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>
                            #{t.orden}
                          </span>
                        )}
                        <h3 style={{ color: tema.text, fontSize: '16px', fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>{t.titulo}</h3>
                      </div>
                      {t.descripcion && <p style={{ color: tema.muted, fontSize: '13px', marginBottom: '8px' }}>{t.descripcion}</p>}
                      <p style={{ color: tema.muted, fontSize: '11px' }}>{new Date(t.fecha_subida).toLocaleDateString('es-ES')}</p>
                    </div>
                    <div className="card-actions">
                      {t.archivo_url && (
                        <a
                          href={t.archivo_url?.startsWith('http') ? t.archivo_url : `${window.location.origin}${t.archivo_url}`}
                          target="_blank" rel="noreferrer"
                          style={{
                            background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)',
                            border: 'none', color: '#fff', padding: '9px 18px', borderRadius: '50px',
                            fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
                            boxShadow: '0 4px 14px rgba(236,72,153,0.35)', textDecoration: 'none',
                          }}
                        >
                          📄 Ver PDF
                        </a>
                      )}
                      {esProfesor && (
                        <button
                          onClick={() => eliminar(t.id)}
                          style={{
                            background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)',
                            border: 'none', color: '#fff', padding: '9px 14px', borderRadius: '50px',
                            fontSize: '13px', cursor: 'pointer', fontWeight: 600,
                            boxShadow: '0 4px 14px rgba(231,76,60,0.35)',
                            display: 'flex', alignItems: 'center', gap: '6px',
                          }}
                        >
                          <TrashIcon style={{ width: 16, height: 16 }} /> Borrar
                        </button>
                      )}
                    </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
